import type { DragEvent, FC } from "react";

import { Delete, DragIndicator, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SurveyConfiguration } from "@rubi/api-generated";

import styles from "./SurveyGroupMembersPage.module.scss";

export interface GroupSurveysTabProps {
  onNavigateBack: () => void;
  selectedIds: string[];
  configNameById: Map<string, string>;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent) => void;
  /** Called when a dragged item is dropped on a row; parent should reorder and clear drag state. */
  onDropOnItem: (targetId: string) => void;
  onRemoveSurvey: (id: string) => void;
  availableCount: number;
  search: string;
  onSearchChange: (value: string) => void;
  filteredAvailableConfigs: SurveyConfiguration[];
  selectedSet: Set<string>;
  onToggleConfig: (id: string) => void;
  savingSurveys: boolean;
  loading: boolean;
  onSaveSurveys: () => void;
}

const GroupSurveysTab: FC<GroupSurveysTabProps> = ({
  onNavigateBack,
  selectedIds,
  configNameById,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDropOnItem,
  onRemoveSurvey,
  availableCount,
  search,
  onSearchChange,
  filteredAvailableConfigs,
  selectedSet,
  onToggleConfig,
  savingSurveys,
  loading,
  onSaveSurveys,
}) => {
  return (
    <>
      <Paper variant="outlined" className={styles.surveysIntroPaper}>
        <Typography variant="body2" color="text.secondary">
          Select surveys for this group, then drag selected surveys to set
          display order.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            label={`${selectedIds.length} selected`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${availableCount} available`}
            color="default"
            variant="outlined"
          />
        </Stack>
      </Paper>

      <Stack spacing={2}>
        <Paper variant="outlined" className={styles.surveyPanelPaper}>
          <Typography variant="subtitle2" className={styles.panelSubtitle}>
            Selected surveys ({selectedIds.length})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Drag to reorder display priority.
          </Typography>
          <Divider className={styles.sectionDivider} />
          <List dense disablePadding className={styles.selectedList}>
            {selectedIds.map((id, index) => (
              <ListItem
                key={id}
                draggable
                onDragStart={() => onDragStart(id)}
                onDragOver={onDragOver}
                onDrop={() => {
                  onDropOnItem(id);
                  onDragEnd();
                }}
                onDragEnd={onDragEnd}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => onRemoveSurvey(id)}
                    aria-label="Remove survey"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                }
                className={styles.draggableListItem}
              >
                <DragIndicator fontSize="small" className={styles.dragIcon} />
                <ListItemText
                  primary={`${index + 1}. ${configNameById.get(id) ?? id}`}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItem>
            ))}
            {selectedIds.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                className={styles.emptyHint}
              >
                No surveys selected yet.
              </Typography>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" className={styles.surveyPanelPaper}>
          <Typography variant="subtitle2" className={styles.panelSubtitle}>
            Available surveys ({availableCount})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Search and select surveys to add to this group.
          </Typography>
          <Divider className={styles.sectionDivider} />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search surveys"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            className={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    className={styles.searchAdornmentIcon}
                  />
                </InputAdornment>
              ),
            }}
          />
          <FormGroup className={styles.availableFormGroup}>
            {filteredAvailableConfigs.map((config) => (
              <FormControlLabel
                key={config.id}
                control={
                  <Checkbox
                    checked={selectedSet.has(config.id)}
                    onChange={() => onToggleConfig(config.id)}
                  />
                }
                label={config.name}
              />
            ))}
            {filteredAvailableConfigs.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                className={styles.emptyHint}
              >
                No surveys match your search.
              </Typography>
            )}
          </FormGroup>
        </Paper>
      </Stack>

      <Box className={styles.stickyActions}>
        <Button onClick={() => onNavigateBack()}>Cancel</Button>
        <Button
          onClick={onSaveSurveys}
          variant="contained"
          disabled={savingSurveys || loading}
        >
          Save
        </Button>
      </Box>
    </>
  );
};

export default GroupSurveysTab;
